/**
 * Module Tarifs — Grilles tarifaires format Excel éditable.
 * Vue liste + éditeur Excel-like pour créer/modifier une grille.
 * Actions par grille : visualiser, télécharger PDF, imprimer, envoyer, supprimer.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { TypeTarifCards, PanelPriceCards, EvolutionPrix, ServicesCard } from '../components/tarifs/TarifsOverview';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DollarSign, Plus, FileText, Download, Mail, Trash2, Printer, Eye, ArrowLeft, Save, X as XIcon, PlusCircle, CheckCircle2, Percent, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_META = {
  'Panneau déroulant': { label: 'Panneaux déroulants',    bgHead: 'bg-blue-50',    text: 'text-blue-900',    border: 'border-blue-200'   },
  'Panneau LED':       { label: 'Panneaux LED intérieur', bgHead: 'bg-emerald-50', text: 'text-emerald-900', border: 'border-emerald-200'},
  'Grand format':      { label: 'Panneaux grand format',  bgHead: 'bg-amber-50',   text: 'text-amber-900',   border: 'border-amber-200'  },
  'Panneau poteau':    { label: 'Affiches sur poteaux',   bgHead: 'bg-cyan-50',    text: 'text-cyan-900',    border: 'border-cyan-200'   },
};
const SECTION_ORDER = ['Panneau déroulant', 'Panneau LED', 'Grand format', 'Panneau poteau'];

const DEFAULT_ROWS = {
  'Panneau déroulant': [
    { label: '1 affiche / 1 panneau',              prix_jour: 4000,   prix_mois: 120000,   prix_trimestre: 0, prix_annee: 1460000,   remarques: '' },
    { label: '1 panneau • 10 affiches',            prix_jour: 35000,  prix_mois: 1050000,  prix_trimestre: 0, prix_annee: 1277500,   remarques: '' },
    { label: '10 empl / 10 panneaux',              prix_jour: 250000, prix_mois: 7500000,  prix_trimestre: 0, prix_annee: 0,         remarques: '' },
  ],
  'Panneau LED': [
    { label: '1 affiche / 1 panneau',              prix_jour: 2500,   prix_mois: 75000,    prix_trimestre: 0, prix_annee: 912500,    remarques: '' },
    { label: '1 panneau • 10 affiches',            prix_jour: 20000,  prix_mois: 600000,   prix_trimestre: 0, prix_annee: 7300000,   remarques: '' },
    { label: '10 empl / 10 panneaux',              prix_jour: 45000,  prix_mois: 1350000,  prix_trimestre: 0, prix_annee: 0,         remarques: '' },
  ],
  'Grand format': [
    { label: '1 affiche double / 1 panneau',       prix_jour: 500,    prix_mois: 15000,    prix_trimestre: 0, prix_annee: 182500,    remarques: '' },
    { label: '1 emplacement',                      prix_jour: 12500,  prix_mois: 375000,   prix_trimestre: 0, prix_annee: 4562000,   remarques: '' },
    { label: '4 emplacements',                     prix_jour: 50000,  prix_mois: 150000,   prix_trimestre: 0, prix_annee: 0,         remarques: '' },
  ],
  'Panneau poteau': [
    { label: '1 affiche (video) / 1 panneau',      prix_jour: 20000,  prix_mois: 1800000,  prix_trimestre: 0, prix_annee: 7300000,   remarques: '' },
    { label: '10 affiches (video) / 1 panneau',    prix_jour: 150000, prix_mois: 13500000, prix_trimestre: 0, prix_annee: 0,         remarques: '' },
  ],
};

const fmtRef = () => `GT-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 900) + 100)}`;
const fmtValidite = () => {
  const d = new Date();
  d.setMonth(11); d.setDate(31);
  return `Jusqu'au ${d.toLocaleDateString('fr-FR')}`;
};

const TARIF_TYPES = [
  {
    key: 'normal',
    title: 'Tarif normal',
    letter: 'N',
    description: 'Prix mensuel fixe par type de panneau',
    bullets: ['Durée minimale', 'remises', 'pose et impression'],
    bg: 'bg-blue-600', bgSoft: 'bg-white',
    btn: 'bg-blue-600 hover:bg-blue-700',
    accent: 'text-blue-600', iconBg: 'bg-blue-600',
    ring: 'hover:ring-2 hover:ring-blue-300',
  },
  {
    key: 'proportionnel',
    title: 'Tarif proportionnel',
    letter: '%',
    description: 'Prix au prorata de la surface et de la durée',
    bullets: ['Prix au m² par mois', 'calcul automatique'],
    bg: 'bg-emerald-500', bgSoft: 'bg-white',
    btn: 'bg-emerald-500 hover:bg-emerald-600',
    accent: 'text-emerald-600', iconBg: 'bg-emerald-500',
    ring: 'hover:ring-2 hover:ring-emerald-300',
  },
  {
    key: 'or',
    title: 'Tarif or',
    letter: '★',
    description: 'Offre premium pour emplacements stratégiques et écrans LED',
    bullets: ['Prix négociés', 'visibilité maximale'],
    bg: 'bg-gradient-to-br from-amber-400 to-yellow-500', bgSoft: 'bg-gradient-to-br from-amber-50 to-yellow-50',
    btn: 'bg-yellow-500 hover:bg-yellow-600',
    accent: 'text-amber-600', iconBg: 'bg-gradient-to-br from-amber-400 to-yellow-500',
    ring: 'hover:ring-2 hover:ring-amber-300 ring-2 ring-amber-200',
  },
];

export default function Tarifs({ user, onLogout }) {
  const [grids, setGrids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | { typeTarif, grid? }
  const [previewGrid, setPreviewGrid] = useState(null);
  const [sendGrid, setSendGrid] = useState(null);
  const [listTypeTarif, setListTypeTarif] = useState(null); // Afficher les grilles d'un type

  const loadGrids = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/tarifs`);
      setGrids(r.data || []);
    } catch {
      toast.error('Impossible de charger les grilles');
    } finally { setLoading(false); }
  };

  const [overview, setOverview] = useState(null);
  const loadOverview = () => axios.get(`${API}/api/tarifs/overview`).then((r) => setOverview(r.data)).catch(() => {});
  useEffect(() => { loadGrids(); loadOverview(); }, []);

  const openEditor = (typeTarif, grid = null) => {
    setEditing({ typeTarif, grid });
  };

  const countByType = (typeKey) => grids.filter(g => (g.type_tarif || 'normal') === typeKey).length;

  const deleteGrid = async (g) => {
    if (!window.confirm(`Supprimer la grille « ${g.nom} » ?`)) return;
    try {
      await axios.delete(`${API}/api/tarifs/${g.id}`);
      toast.success('Grille supprimée');
      loadGrids();
    } catch { toast.error('Impossible de supprimer'); }
  };

  const downloadPdf = async (g) => {
    try {
      const r = await axios.get(`${API}/api/tarifs/${g.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${g.nom || 'grille'}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur téléchargement'); }
  };

  const printPdf = async (g) => {
    try {
      const r = await axios.get(`${API}/api/tarifs/${g.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, '_blank');
      if (w) w.onload = () => w.print();
    } catch { toast.error('Erreur impression'); }
  };

  const openPreview = async (g) => setPreviewGrid(g);

  // ─────── ÉDITEUR ───────
  if (editing) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="tarifs">
        <TarifEditor
          existingGrid={editing.grid || null}
          typeTarif={editing.typeTarif || 'normal'}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadGrids(); loadOverview(); }}
        />
      </Layout>
    );
  }

  // ─────── LISTE DES GRILLES D'UN TYPE ───────
  if (listTypeTarif) {
    const t = TARIF_TYPES.find(x => x.key === listTypeTarif);
    const typeGrids = grids.filter(g => (g.type_tarif || 'normal') === listTypeTarif);
    return (
      <Layout user={user} onLogout={onLogout} currentPage="tarifs">
        <div className="space-y-5" data-testid="tarifs-list-type">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => setListTypeTarif(null)} className="gap-2">
                <ArrowLeft className="h-4 w-4" /> Retour
              </Button>
              <div className={`w-12 h-12 rounded-xl ${t.iconBg} flex items-center justify-center text-white text-xl font-black shadow-md`}>{t.letter}</div>
              <div>
                <h1 className="text-2xl font-extrabold text-slate-900">{t.title}</h1>
                <p className="text-sm text-slate-500">{typeGrids.length} grille{typeGrids.length > 1 ? 's' : ''} créée{typeGrids.length > 1 ? 's' : ''}</p>
              </div>
            </div>
            <Button onClick={() => openEditor(listTypeTarif)} className={`${t.btn} gap-2 h-10 text-white`} data-testid="btn-create-grid-of-type">
              <Plus className="h-4 w-4" /> Créer une grille
            </Button>
          </div>

          {typeGrids.length === 0 ? (
            <Card className="border border-dashed border-slate-300 bg-slate-50">
              <CardContent className="p-12 text-center">
                <FileText className="h-14 w-14 text-slate-300 mx-auto mb-3" />
                <div className="text-lg font-semibold text-slate-700">Aucune grille créée pour ce type</div>
                <div className="text-sm text-slate-500 mt-1">Cliquez sur « Créer une grille » pour commencer.</div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {typeGrids.map(g => (
                <Card key={g.id} className="border border-slate-200 hover:shadow-lg transition-shadow" data-testid={`grid-card-${g.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg ${t.iconBg} flex items-center justify-center flex-shrink-0 text-white font-bold`}>
                        {t.letter}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 truncate">{g.nom}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Réf. {g.reference || '—'} · {(g.sections || []).reduce((n, s) => n + (s.items?.length || 0), 0)} lignes
                        </div>
                      </div>
                    </div>
                    {g.description && <div className="text-xs text-slate-600 mb-3 line-clamp-2">{g.description}</div>}
                    <div className="text-[11px] text-slate-500 mb-3">
                      {g.validite || 'Validité non renseignée'}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => openPreview(g)} className="h-8 gap-1" data-testid={`btn-preview-${g.id}`}>
                        <Eye className="h-3.5 w-3.5" /> Voir
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(g)} className="h-8 gap-1" data-testid={`btn-download-${g.id}`}>
                        <Download className="h-3.5 w-3.5" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => printPdf(g)} className="h-8 gap-1" data-testid={`btn-print-${g.id}`}>
                        <Printer className="h-3.5 w-3.5" /> Imprimer
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setSendGrid(g)} className="h-8 gap-1" data-testid={`btn-send-${g.id}`}>
                        <Mail className="h-3.5 w-3.5" /> Envoyer
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEditor(listTypeTarif, g)} className="h-8 gap-1">
                        <FileText className="h-3.5 w-3.5" /> Modifier
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteGrid(g)} className="h-8 gap-1 text-red-600 border-red-200 hover:bg-red-50" data-testid={`btn-delete-${g.id}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Supprimer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        {previewGrid && <PreviewDialog grid={previewGrid} onClose={() => setPreviewGrid(null)} />}
        {sendGrid && <SendEmailDialog grid={sendGrid} onClose={() => setSendGrid(null)} />}
      </Layout>
    );
  }

  // ─────── VUE D'ENSEMBLE ───────
  return (
    <Layout user={user} onLogout={onLogout} currentPage="tarifs">
      <div className="space-y-4" data-testid="tarifs-root">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Tarifs</h1>
            <p className="text-slate-500">Choisissez un type de tarif, fixez les prix par panneau et suivez leur évolution</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setListTypeTarif('normal')} className="h-11 px-5 font-bold text-blue-700 bg-white" data-testid="btn-mes-grilles">Mes grilles tarifaires</Button>
            <Button onClick={() => openEditor('normal')} className="h-11 px-5 font-bold bg-blue-600 hover:bg-blue-700 gap-1" data-testid="btn-create-grid-main"><Plus className="h-4 w-4" /> Créer une grille</Button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-slate-400">Chargement…</div>
        ) : (
          <>
            <TypeTarifCards types={TARIF_TYPES} countByType={countByType} onCreate={openEditor} onList={setListTypeTarif} />
            <PanelPriceCards
              overview={overview}
              onView={(t) => { const g = grids.find((x) => x.id === t.last_grid_id); if (g) openPreview(g); else toast.info('Aucune grille pour ce type'); }}
              onEdit={(t) => { const g = grids.find((x) => x.id === t.last_grid_id); openEditor(g?.type_tarif || 'normal', g || null); }}
            />
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-4 items-start">
              <EvolutionPrix overview={overview} />
              <ServicesCard overview={overview} onChanged={loadOverview} />
            </div>
          </>
        )}
      </div>

      {previewGrid && <PreviewDialog grid={previewGrid} onClose={() => setPreviewGrid(null)} />}
      {sendGrid && <SendEmailDialog grid={sendGrid} onClose={() => setSendGrid(null)} />}
    </Layout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Éditeur Excel-like
// ═══════════════════════════════════════════════════════════════════
function TarifEditor({ existingGrid, typeTarif = 'normal', onCancel, onSaved }) {
  const isEdit = !!existingGrid;
  const effectiveType = existingGrid?.type_tarif || typeTarif;
  const typeMeta = TARIF_TYPES.find(t => t.key === effectiveType) || TARIF_TYPES[0];
  const [nom, setNom] = useState(existingGrid?.nom || `${typeMeta.title} — Grille`);
  const [description, setDescription] = useState(existingGrid?.description || '');
  const [reference, setReference] = useState(existingGrid?.reference || fmtRef());
  const [validite, setValidite] = useState(existingGrid?.validite || fmtValidite());
  const [entrepriseNom, setEntrepriseNom] = useState(existingGrid?.entreprise_nom || 'SESAM');
  const [entrepriseSub, setEntrepriseSub] = useState(existingGrid?.entreprise_sous_titre || 'Régie d\'affichage publicitaire');

  const [coordNom, setCoordNom] = useState(existingGrid?.coord_nom || 'SESAM CRM SOLUTIONS');
  const [coordAdr, setCoordAdr] = useState(existingGrid?.coord_adresse || 'Avenue Boganda, Bangui – RCA');
  const [coordTel, setCoordTel] = useState(existingGrid?.coord_telephone || '75 50 37 02 / 74 36 11 36');
  const [coordEmail, setCoordEmail] = useState(existingGrid?.coord_email || 'sesambangui@gmail.com');
  const [coordSite, setCoordSite] = useState(existingGrid?.coord_site || 'www.sesamrca.com');

  const [conditions, setConditions] = useState(existingGrid?.conditions?.join('\n') || [
    'Tarifs indiqués hors taxes (HT).',
    'Les tarifs peuvent varier selon l\'emplacement, la durée de la campagne et la disponibilité.',
    'Toute demande spécifique peut faire l\'objet d\'un devis personnalisé.',
    'Cette grille tarifaire est indicative et non contractuelle.',
  ].join('\n'));

  // Sections : Map<type, items[]>
  const [sectionsData, setSectionsData] = useState(() => {
    const initial = {};
    SECTION_ORDER.forEach(t => { initial[t] = DEFAULT_ROWS[t].map(r => ({ ...r })); });
    (existingGrid?.sections || []).forEach(s => {
      if (SECTION_ORDER.includes(s.type_panneau) && s.items?.length) {
        initial[s.type_panneau] = s.items.map(it => ({
          label: it.label || '',
          prix_jour:      Number(it.prix_jour) || 0,
          prix_mois:      Number(it.prix_mois) || 0,
          prix_trimestre: Number(it.prix_trimestre) || 0,
          prix_annee:     Number(it.prix_annee) || 0,
          remarques:      it.remarques || '',
        }));
      }
    });
    return initial;
  });

  const [saving, setSaving] = useState(false);

  const updateCell = (type, idx, field, value) => {
    setSectionsData(prev => {
      const next = { ...prev };
      const rows = [...next[type]];
      rows[idx] = { ...rows[idx], [field]: value };
      next[type] = rows;
      return next;
    });
  };

  const addRow = (type) => {
    setSectionsData(prev => ({
      ...prev,
      [type]: [...prev[type], { label: '', prix_jour: 0, prix_mois: 0, prix_trimestre: 0, prix_annee: 0, remarques: '' }],
    }));
  };

  const removeRow = (type, idx) => {
    setSectionsData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    if (!nom.trim()) { toast.error('Nommez votre grille'); return; }
    setSaving(true);
    const payload = {
      nom, description, reference, validite,
      type_tarif: effectiveType,
      entreprise_nom: entrepriseNom, entreprise_sous_titre: entrepriseSub,
      coord_nom: coordNom, coord_adresse: coordAdr, coord_telephone: coordTel,
      coord_email: coordEmail, coord_site: coordSite,
      conditions: conditions.split('\n').map(l => l.trim()).filter(Boolean),
      sections: SECTION_ORDER.map(t => ({
        type_panneau: t,
        items: sectionsData[t].filter(r => r.label.trim() || r.prix_jour || r.prix_mois || r.prix_trimestre || r.prix_annee),
      })),
    };
    try {
      if (isEdit) {
        await axios.put(`${API}/api/tarifs/${existingGrid.id}`, payload);
      } else {
        await axios.post(`${API}/api/tarifs`, payload);
      }
      toast.success(isEdit ? 'Grille mise à jour' : 'Grille créée');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  const previewPdf = async () => {
    const payload = {
      nom, description, reference, validite,
      type_tarif: effectiveType,
      entreprise_nom: entrepriseNom, entreprise_sous_titre: entrepriseSub,
      coord_nom: coordNom, coord_adresse: coordAdr, coord_telephone: coordTel,
      coord_email: coordEmail, coord_site: coordSite,
      conditions: conditions.split('\n').map(l => l.trim()).filter(Boolean),
      sections: SECTION_ORDER.map(t => ({
        type_panneau: t,
        items: sectionsData[t].filter(r => r.label.trim()),
      })),
    };
    try {
      const r = await axios.post(`${API}/api/tarifs/preview-pdf`, payload, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('Erreur aperçu'); }
  };

  return (
    <div className="space-y-4 pb-8" data-testid="tarif-editor">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap sticky top-0 bg-slate-50/90 backdrop-blur-md z-20 py-3 -mx-3 sm:-mx-6 px-3 sm:px-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onCancel} className="gap-2" data-testid="editor-cancel">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${typeMeta.iconBg} text-white font-bold text-sm shadow-sm`}>
            <span className="text-base leading-none">{typeMeta.letter}</span>
            {typeMeta.title}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={previewPdf} className="gap-2 h-10" data-testid="editor-preview">
            <Eye className="h-4 w-4" /> Aperçu PDF
          </Button>
          <Button onClick={save} disabled={saving} className={`${typeMeta.btn} gap-2 h-10 text-white`} data-testid="editor-save">
            <Save className="h-4 w-4" /> {saving ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer la grille')}
          </Button>
        </div>
      </div>

      {/* En-tête grille (mockup header) */}
      <Card className="border border-slate-200">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Votre entreprise</div>
              <Input value={entrepriseNom} onChange={(e) => setEntrepriseNom(e.target.value)} className="font-bold text-slate-900 h-11" placeholder="Nom de votre entreprise" data-testid="editor-entreprise-nom" />
              <Input value={entrepriseSub} onChange={(e) => setEntrepriseSub(e.target.value)} className="mt-2 h-9 text-sm" placeholder="Sous-titre" data-testid="editor-entreprise-sub" />
            </div>
            <div className="text-center">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Nom de la grille</div>
              <Input value={nom} onChange={(e) => setNom(e.target.value)} className="text-center font-bold text-lg h-11 bg-blue-50 border-blue-200" data-testid="editor-nom" />
              <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 h-9 text-sm text-center italic" placeholder="Description (optionnel)" data-testid="editor-description" />
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-semibold text-slate-500">Date de création</label>
                <Input value={new Date().toLocaleDateString('fr-FR')} disabled className="h-8 bg-slate-50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Référence</label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} className="h-8" data-testid="editor-reference" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Validité</label>
                <Input value={validite} onChange={(e) => setValidite(e.target.value)} className="h-8" data-testid="editor-validite" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tableau Excel-like */}
      <Card className="border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="p-3 text-center font-bold w-32 border-r border-slate-700">Catégorie</th>
                <th className="p-3 text-left font-bold border-r border-slate-700">Description / Caractéristiques</th>
                <th className="p-3 text-center font-bold bg-emerald-700 border-r border-slate-700 w-28">Journalier<br/><span className="text-[10px] font-normal opacity-80">(FCFA HT)</span></th>
                <th className="p-3 text-center font-bold bg-emerald-700 border-r border-slate-700 w-28">Mensuel<br/><span className="text-[10px] font-normal opacity-80">(FCFA HT)</span></th>
                <th className="p-3 text-center font-bold bg-emerald-700 border-r border-slate-700 w-28">Trimestre<br/><span className="text-[10px] font-normal opacity-80">(FCFA HT)</span></th>
                <th className="p-3 text-center font-bold bg-emerald-700 border-r border-slate-700 w-28">Annuel<br/><span className="text-[10px] font-normal opacity-80">(FCFA HT)</span></th>
                <th className="p-3 text-center font-bold w-52">Remarques</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {SECTION_ORDER.map(type => {
                const meta = SECTION_META[type];
                const rows = sectionsData[type] || [];
                return rows.map((row, idx) => (
                  <tr key={`${type}-${idx}`} className="hover:bg-slate-50">
                    {idx === 0 && (
                      <td rowSpan={rows.length} className={`${meta.bgHead} ${meta.text} ${meta.border} border text-center font-bold p-2 align-middle`}>
                        <div>{meta.label.split(' ').map((w, i) => <div key={i}>{w}</div>)}</div>
                      </td>
                    )}
                    <td className="border border-slate-200 p-0">
                      <Input value={row.label} onChange={(e) => updateCell(type, idx, 'label', e.target.value)}
                        className="h-10 border-0 rounded-none focus-visible:ring-1 focus-visible:ring-blue-400 focus-visible:ring-inset"
                        placeholder="Description…"
                        data-testid={`cell-${type}-${idx}-label`} />
                    </td>
                    {['prix_jour', 'prix_mois', 'prix_trimestre', 'prix_annee'].map(f => (
                      <td key={f} className="border border-slate-200 p-0">
                        <Input type="number" min="0" value={row[f] || ''}
                          onChange={(e) => updateCell(type, idx, f, Number(e.target.value) || 0)}
                          className="h-10 border-0 rounded-none text-right pr-3 focus-visible:ring-1 focus-visible:ring-blue-400 focus-visible:ring-inset tabular-nums"
                          placeholder="0"
                          data-testid={`cell-${type}-${idx}-${f}`} />
                      </td>
                    ))}
                    <td className="border border-slate-200 p-0">
                      <Input value={row.remarques} onChange={(e) => updateCell(type, idx, 'remarques', e.target.value)}
                        className="h-10 border-0 rounded-none focus-visible:ring-1 focus-visible:ring-blue-400 focus-visible:ring-inset"
                        placeholder="—"
                        data-testid={`cell-${type}-${idx}-remarques`} />
                    </td>
                    <td className="border border-slate-200 p-0 text-center">
                      <button
                        onClick={() => removeRow(type, idx)}
                        className="w-8 h-8 rounded hover:bg-red-50 text-red-500 flex items-center justify-center mx-auto"
                        title="Supprimer la ligne"
                        data-testid={`btn-remove-${type}-${idx}`}
                      >
                        <XIcon className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                )).concat([
                  <tr key={`${type}-add`}>
                    <td colSpan={8} className="p-0">
                      <button
                        onClick={() => addRow(type)}
                        className={`w-full h-8 flex items-center justify-center gap-1 text-xs font-medium text-slate-500 hover:bg-slate-50 border-t ${meta.border}`}
                        data-testid={`btn-add-${type}`}
                      >
                        <PlusCircle className="h-3.5 w-3.5" /> Ajouter une ligne "{meta.label}"
                      </button>
                    </td>
                  </tr>
                ]);
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Conditions + coordonnées */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border border-slate-200 bg-slate-50">
          <CardContent className="p-5">
            <label className="text-sm font-bold text-slate-900 mb-2 block">Conditions générales</label>
            <Textarea rows={5} value={conditions} onChange={(e) => setConditions(e.target.value)}
              placeholder="Une condition par ligne…" className="bg-white text-sm" data-testid="editor-conditions" />
            <div className="text-[11px] text-slate-500 mt-1">Une condition par ligne (elle sera affichée en bullet dans le PDF).</div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 bg-slate-50">
          <CardContent className="p-5">
            <label className="text-sm font-bold text-slate-900 mb-2 block">Vos coordonnées</label>
            <div className="space-y-2">
              <Input value={coordNom} onChange={(e) => setCoordNom(e.target.value)} placeholder="Nom de l'entreprise" className="h-9 bg-white text-sm" data-testid="editor-coord-nom" />
              <Input value={coordAdr} onChange={(e) => setCoordAdr(e.target.value)} placeholder="Adresse" className="h-9 bg-white text-sm" data-testid="editor-coord-adr" />
              <Input value={coordTel} onChange={(e) => setCoordTel(e.target.value)} placeholder="Téléphone" className="h-9 bg-white text-sm" data-testid="editor-coord-tel" />
              <Input value={coordEmail} onChange={(e) => setCoordEmail(e.target.value)} placeholder="Email" className="h-9 bg-white text-sm" data-testid="editor-coord-email" />
              <Input value={coordSite} onChange={(e) => setCoordSite(e.target.value)} placeholder="Site web" className="h-9 bg-white text-sm" data-testid="editor-coord-site" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Dialog aperçu (PDF embed)
// ═══════════════════════════════════════════════════════════════════
function PreviewDialog({ grid, onClose }) {
  const [pdfUrl, setPdfUrl] = useState('');
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/api/tarifs/${grid.id}/pdf`, { responseType: 'blob' }).then((r) => {
      if (!mounted) return;
      setPdfUrl(URL.createObjectURL(r.data));
    });
    return () => { mounted = false; if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
    // eslint-disable-next-line
  }, [grid.id]);
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b">
          <DialogTitle>{grid.nom}</DialogTitle>
        </DialogHeader>
        <div className="h-[80vh] bg-slate-100">
          {pdfUrl ? (
            <iframe src={pdfUrl} className="w-full h-full" title="Grille tarifaire" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sm text-slate-400">Chargement…</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Dialog envoi email
// ═══════════════════════════════════════════════════════════════════
function SendEmailDialog({ grid, onClose }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!email) { toast.error('Email destinataire requis'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/tarifs/${grid.id}/send-email`, {
        recipient_email: email, recipient_name: name, message,
      });
      toast.success(r.data?.message || 'Envoyé');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Envoyer « {grid.nom} » par email</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Nom du destinataire</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Cher Client" data-testid="send-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Email *</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@exemple.com" data-testid="send-email" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Message (optionnel)</label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Un mot personnel…" data-testid="send-msg" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={send} disabled={sending} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="send-submit">
            <Mail className="h-4 w-4" /> {sending ? 'Envoi…' : 'Envoyer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
